/**
 * Track Order — Frontend Controller
 * Vanilla ES6+, no dependencies. Progressive enhancement over a plain <form>.
 */
(() => {
  "use strict";

  const STEP_ORDER = ["received", "processing", "shipped", "in_transit", "delivered"];

  // Map various backend status strings to a timeline step key.
  function statusToStep(status) {
    const s = (status || "").toLowerCase();
    if (s.includes("deliver")) return "delivered";
    if (s.includes("transit")) return "in_transit";
    if (s.includes("ship")) return "shipped";
    if (s.includes("process") || s.includes("unfulfilled")) return "processing";
    return "received";
  }

  function isValidEmail(value) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  }

  function initTrackOrder(root) {
    const form = root.querySelector("[data-track-form]");
    const orderInput = root.querySelector("[data-order-number]");
    const emailInput = root.querySelector("[data-email]");
    const errorOrderEl = root.querySelector("[data-error-order-number]");
    const errorEmailEl = root.querySelector("[data-error-email]");

    const submitBtn = root.querySelector("[data-submit-btn]");
    const btnText = root.querySelector("[data-btn-text]");
    const btnSpinner = root.querySelector("[data-btn-spinner]");

    const skeleton = root.querySelector("[data-skeleton]");
    const resultCard = root.querySelector("[data-result]");
    const errorCard = root.querySelector("[data-error-card]");
    const retryBtn = root.querySelector("[data-retry-btn]");

    const appProxyUrl = root.dataset.appProxyUrl || "/apps/track-order";

    function resetPanels() {
      skeleton.hidden = true;
      resultCard.hidden = true;
      errorCard.hidden = true;
    }

    function setLoading(isLoading) {
      submitBtn.disabled = isLoading;
      btnSpinner.hidden = !isLoading;
      btnText.textContent = isLoading ? "Tracking..." : (root.dataset.buttonText || "Track Order");
      if (isLoading) {
        resetPanels();
        skeleton.hidden = false;
      }
    }

    function validate() {
      let valid = true;
      errorOrderEl.textContent = "";
      errorEmailEl.textContent = "";
      orderInput.removeAttribute("aria-invalid");
      emailInput.removeAttribute("aria-invalid");

      if (!orderInput.value.trim()) {
        errorOrderEl.textContent = "Please enter your order number.";
        orderInput.setAttribute("aria-invalid", "true");
        valid = false;
      }

      if (!emailInput.value.trim()) {
        errorEmailEl.textContent = "Please enter your email address.";
        emailInput.setAttribute("aria-invalid", "true");
        valid = false;
      } else if (!isValidEmail(emailInput.value.trim())) {
        errorEmailEl.textContent = "Please enter a valid email address.";
        emailInput.setAttribute("aria-invalid", "true");
        valid = false;
      }

      return valid;
    }

    function renderTimeline(currentStatus) {
      const currentStep = statusToStep(currentStatus);
      const currentIndex = STEP_ORDER.indexOf(currentStep);

      root.querySelectorAll("[data-step]").forEach((stepEl) => {
        const stepKey = stepEl.dataset.step;
        const stepIndex = STEP_ORDER.indexOf(stepKey);
        stepEl.classList.remove("is-complete", "is-current");
        if (stepIndex < currentIndex) {
          stepEl.classList.add("is-complete");
        } else if (stepIndex === currentIndex) {
          stepEl.classList.add("is-complete", "is-current");
        }
      });
    }

    function renderResult(data) {
      resetPanels();
      resultCard.hidden = false;

      root.querySelector("[data-result-order-number]").textContent = data.orderNumber || "";
      root.querySelector("[data-result-customer]").textContent = data.customer || "";
      root.querySelector("[data-result-status]").textContent = data.currentStatus || "Processing";
      root.querySelector("[data-result-date]").textContent = data.orderDate || "—";
      root.querySelector("[data-result-payment]").textContent = data.paymentStatus || "—";
      root.querySelector("[data-result-fulfillment]").textContent = data.fulfillmentStatus || "—";
      root.querySelector("[data-result-shipping-method]").textContent = data.shippingMethod || "—";
      root.querySelector("[data-result-carrier]").textContent = data.carrier || "—";
      root.querySelector("[data-result-tracking-number]").textContent = data.trackingNumber || "—";

      const etaRow = root.querySelector("[data-estimated-delivery-row]");
      if (data.estimatedDelivery) {
        etaRow.hidden = false;
        root.querySelector("[data-result-eta]").textContent = data.estimatedDelivery;
      } else {
        etaRow.hidden = true;
      }

      const ctaEl = root.querySelector("[data-result-tracking-url]");
      const notice = root.querySelector("[data-processing-notice]");
      const hasShipped = Boolean(data.trackingUrl);

      if (hasShipped) {
        ctaEl.href = data.trackingUrl;
        ctaEl.hidden = false;
        notice.hidden = true;
      } else {
        ctaEl.hidden = true;
        notice.hidden = false;
      }

      renderTimeline(data.currentStatus || data.fulfillmentStatus);
    }

    function renderError(message) {
      resetPanels();
      errorCard.hidden = false;
      root.querySelector("[data-error-message]").textContent =
        message || "Please double-check your order number and email address, then try again.";
    }

    async function trackOrder(orderNumber, email) {
      setLoading(true);
      try {
        const params = new URLSearchParams({ orderNumber, email });
        const response = await fetch(`${appProxyUrl}?${params.toString()}`, {
          method: "GET",
          headers: { Accept: "application/json" },
        });

        if (!response.ok) {
          if (response.status === 429) {
            renderError("Too many attempts. Please wait a moment and try again.");
            return;
          }
          if (response.status === 404) {
            renderError("We couldn't find an order matching those details.");
            return;
          }
          if (response.status === 401 || response.status === 403) {
            renderError("Those details don't match our records. Please check and try again.");
            return;
          }
          renderError();
          return;
        }

        const data = await response.json();

        if (!data.success) {
          renderError(data.message);
          return;
        }

        renderResult(data);
      } catch (err) {
        renderError("Something went wrong. Please check your connection and try again.");
      } finally {
        setLoading(false);
      }
    }

    form.addEventListener("submit", (event) => {
      event.preventDefault();
      if (!validate()) return;
      trackOrder(orderInput.value.trim(), emailInput.value.trim());
    });

    retryBtn.addEventListener("click", () => {
      resetPanels();
      orderInput.focus();
    });
  }

  function init() {
    document.querySelectorAll("[data-track-order-root]").forEach(initTrackOrder);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();